import { APISurveyAttributes, ArdoqId, AssetType } from '@ardoq/api-types';
import { formatDateMedium } from '@ardoq/date-time';
import { TextOverflow, WithPopover } from '@ardoq/popovers';
import { Column, Row, Table, tableOperations } from '@ardoq/table';
import { Link } from '@ardoq/link';
import styled from 'styled-components';
import { trackOpenExistingSurvey } from '../../surveyAdmin/tracking';
import { surveyAccessControlInterface } from '../../resourcePermissions/accessControlHelpers/surveys';
import { getFeatureContext } from 'streams/featureContext$';
import { type Locale } from '@ardoq/locale';
import {
  getContactEmailRender,
  getSurveyStatusRender,
} from '../../surveyAdmin/SurveyOverview/columns';
import { t, type I18nLocale } from '@ardoq/i18n';

const StyledTextOverflow = styled(TextOverflow)`
  width: 190px;
`;

const SURVEY_LINK = '/app/survey-admin/edit/';

type AffectedEntityType = AssetType.SURVEY;

type AffectedEntities = APISurveyAttributes[];

const getSurveyLink = (id: ArdoqId, i18nLocale: I18nLocale) => {
  const hasEditAccess = surveyAccessControlInterface.canEditSurvey(
    getFeatureContext(),
    id
  );
  return (
    <WithPopover
      content={!hasEditAccess && 'You do not have access to edit this survey'}
    >
      <Link
        onClick={() => {
          if (hasEditAccess) {
            trackOpenExistingSurvey(id, 'deleteAssetModal');
            window.open(`${SURVEY_LINK}${id}`, '_blank');
          }
        }}
        type={hasEditAccess ? 'primary' : 'secondary'}
      >
        {t(i18nLocale, 'deleteAssetModal:affectedSurveys.open', 'Open')}
      </Link>
    </WithPopover>
  );
};

type SurveyRowData = {
  name: { value: string };
  status: { published: boolean };
  contactEmail: { email: string | undefined };
  lastUpdated: string;
  actions: { id: string };
};

const getSurveyColumns = (i18nLocale: I18nLocale): Column<SurveyRowData>[] => [
  {
    id: 'name',
    title: 'Survey name',
    cellRenderer: ({ value }) => (
      <StyledTextOverflow renderIn="popover">{value}</StyledTextOverflow>
    ),
    valueGetter: ({ value }) => value,
  },
  {
    id: 'status',
    title: 'Status',
    cellRenderer: ({ published }) =>
      getSurveyStatusRender(published, i18nLocale),
    valueGetter: ({ published }) => (published ? 'Live' : 'Draft'),
  },
  {
    id: 'contactEmail',
    title: 'Contact email',
    cellRenderer: ({ email }) => getContactEmailRender(email, i18nLocale),
    valueGetter: ({ email }) => email ?? '',
  },
  {
    id: 'lastUpdated',
    title: 'Last updated',
  },
  {
    id: 'actions',
    title: '',
    cellRenderer: ({ id }) => getSurveyLink(id, i18nLocale),
    valueGetter: ({ id }) => id,
  },
];

const toSurveyRows = (
  surveys: APISurveyAttributes[],
  locale: Locale
): Row<SurveyRowData>[] =>
  surveys.map(survey => ({
    id: survey._id,
    data: {
      name: { value: survey.name ?? '' },
      status: { published: survey.published },
      contactEmail: { email: survey.contactEmail },
      lastUpdated: formatDateMedium(survey.lastUpdated, locale),
      actions: { id: survey._id },
    },
  }));

type AffectedEntitiesTableProps = {
  affectedEntities: AffectedEntities;
  affectedEntityType: AffectedEntityType;
  locale: Locale;
  i18nLocale: I18nLocale;
};

/**
 * Currently only supports surveys
 */
const AffectedEntitiesTable = ({
  affectedEntities,
  affectedEntityType,
  locale,
  i18nLocale,
}: AffectedEntitiesTableProps) => {
  switch (affectedEntityType) {
    case AssetType.SURVEY:
      return (
        <Table
          height={`${tableOperations.getEstimatedTableHeight(5)}px`}
          rows={toSurveyRows(affectedEntities, locale)}
          columns={getSurveyColumns(i18nLocale)}
          aria-label={t(
            i18nLocale,
            'deleteAssetModal:affectedSurveys.ariaLabel',
            'Affected surveys'
          )}
        />
      );
  }
};

export { AffectedEntitiesTable };
